// Role classification — the single source of truth for the Dashboard ↔
// ClientHub split (2026-09-14).
//
// The live `public.roles` table seeds MORE roles than the legacy 0/1/2/3 numeric
// mapping catalogued: alongside platform_admin / org_owner / org_admin /
// org_analyst / org_viewer / org_scoped there are three agency-internal staff
// roles — staff_owner, staff_manager, staff_member — that live in the staff
// orgs (Insider Perks, Modern Campground). Verified against the DB via
// scripts/_list-staff-by-org.js:
//
//   Insider Perks org:
//     platform_admin (4), org_owner (1), org_admin (4),
//     staff_owner (1 — Aly Samonte),
//     staff_manager (3 — Ann Sotto, Kendra Veverka, Olivia Sayson),
//     staff_member (8 — Chariss Vergara, Emman Arcenal, …)
//   Modern Campground org:
//     staff_member (4 — Chloe Tayaban, …)
//
// Routing rule (per product):
//   • super admin / admin / owner  (platform_admin, org_owner, org_admin) →
//       a toggle between Dashboard and Client Hub. They land on /dashboard on
//       login and can switch to /hub/now.
//   • staff_owner → ALSO a toggle role: lands on /dashboard on login and can
//       switch to /hub/now. "Owner — full internal access including billing"
//       semantics (2026-10-06): she needs the analytics dashboard, not just the
//       Client Hub. The BFF ROLE_NAME_TO_NUMERIC maps her to tier 2 so the
//       dashboard data endpoints (CDP / Google Ads / GA4 / bookings reads) no
//       longer 403.
//   • staff_manager → ALSO a toggle role (2026-10-07): lands on /dashboard on
//       login and can switch to /hub/now, mirroring staff_owner. Like
//       staff_owner she is NOT an "admin" 0/1 — the numeric stays 2 — so
//       platform-admin-only writes remain gated off; this only grants dashboard
//       access.
//   • staff_member / staff_limited → redirected DIRECTLY to Client Hub
//       (/hub/now) on login, with no option to go to the analytics dashboard.
//       The /dashboard route itself redirects them to /hub/now. staff_limited
//       is the "only explicitly granted clients" variant — the granted-only
//       client set is enforced by RLS on fn_get_my_clients, not by the routing
//       rule here.
//   • org_viewer / org_scoped (clients) → /client-dashboard (unchanged).
//   • org_analyst (manager) → /dashboard (unchanged; not present in the staff
//       orgs, and not part of this rule).

// Roles that get the Dashboard ↔ Client Hub toggle. Mirrors ViewContext's
// `isAdmin` (platform_admin is USER-level; org_owner/org_admin are org-scoped).
// 2026-10-06 — staff_owner added: she lands on /dashboard and may switch to the
// Client Hub, like the other owners. (She is NOT an "admin" 0/1 — the numeric
// stays 2 — so platform-admin-only writes remain gated off; this only grants
// dashboard access.)
// 2026-10-07 — staff_manager added (same rationale as staff_owner): lands on
// /dashboard and may switch to the Client Hub. Numeric stays 2.
export const TOGGLE_ROLE_NAMES = new Set([
  'platform_admin',
  'org_owner',
  'org_admin',
  'staff_owner',
  'staff_manager',
]);

// Agency-internal staff roles that are Client-Hub-only: they land on /hub/now
// and cannot reach the analytics dashboard. staff_owner was removed from this
// set on 2026-10-06 (promoted to a toggle role, above). staff_manager was
// removed on 2026-10-07 (also promoted to a toggle role, above). staff_limited
// (2026-09-28) is the "only explicitly granted clients" variant — same routing
// as the other staff roles (Client Hub, no dashboard); the granted-only client
// set is enforced by RLS on fn_get_my_clients, not by the routing rule here.
export const STAFF_ROLE_NAMES = new Set([
  'staff_member',
  'staff_limited',
]);

// Client-facing roles (unchanged behaviour → /client-dashboard).
export const CLIENT_ROLE_NAMES = new Set([
  'org_viewer',
  'org_scoped',
]);

export const isToggleRoleName = (roleName) =>
  !!roleName && TOGGLE_ROLE_NAMES.has(roleName);

export const isStaffRoleName = (roleName) =>
  !!roleName && STAFF_ROLE_NAMES.has(roleName);

export const isClientRoleName = (roleName) =>
  !!roleName && CLIENT_ROLE_NAMES.has(roleName);

/**
 * Post-login landing route for a primary role name.
 *   client  → /client-dashboard
 *   staff   → /hub/now          (Client-Hub-only staff: staff_member / staff_limited)
 *   toggle  → /dashboard        (super admin / admin / owner / staff_owner / staff_manager)
 *   unknown → /dashboard        (safe default; matches pre-existing behaviour)
 */
export const loginRouteForRole = (roleName) => {
  if (isClientRoleName(roleName)) return '/client-dashboard';
  if (isStaffRoleName(roleName)) return '/hub/now';
  return '/dashboard';
};

/**
 * Whether a user with this role name should be redirected AWAY from the
 * analytics dashboard into Client Hub. True for the Client-Hub-only staff
 * (staff_member / staff_limited) — they have "no option to go to dashboard".
 * staff_owner is NOT blocked (she is a toggle role, 2026-10-06). staff_manager
 * is NOT blocked (also a toggle role, 2026-10-07).
 */
export const dashboardBlockedForRole = (roleName) =>
  isStaffRoleName(roleName);
